import React, { useState } from 'react';
import { Modal, Button, Typography, Box } from '@mui/material';
import { PhoneIphone, Lock, Description, Warning, CheckCircle } from '@mui/icons-material';
import { pad } from 'lodash';

const TermsConditionsModal = ({ open, onClose, onEnquireNow }) => {
  const [isEnquiring, setIsEnquiring] = useState(false);

  const handleBackToOffers = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} sx={modalStyles.container}>
      <Box sx={modalStyles.content}>
        {/* Header Box */}
        <Box sx={modalStyles.header}>
          <Typography variant="h5" sx={modalStyles.title}>Loan Assist – Terms & Conditions</Typography>
          <Typography variant="body1" sx={modalStyles.text}>
            By clicking on “Enquire Now”, you agree and consent to the following:
          </Typography>
        </Box>

        {/* Scrollable Content Box */}
        <Box sx={modalStyles.scrollableContent}>
          <Box sx={modalStyles.termsList}>
            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> 🔐 Consent to Share Information </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              You authorize us to collect and share your personal details, including your name, mobile number, email address, PAN, and other provided information, with our partner banks and financial institutions for the purpose of loan assistance.
            </Typography>

            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> 📞 Consent to be Contacted </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              You agree that partner banks and financial institutions may contact you via phone call, SMS, WhatsApp, or email regarding loan offers and related services.
            </Typography>

            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> 🏦 Facilitation Service Only </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              We act only as a facilitator connecting you with banks and do not provide loans directly. Loan approval, interest rates, and terms are solely determined by the respective bank or financial institution.
            </Typography>

            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> ⚠️ No Guarantee of Approval </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              Submission of an enquiry does not guarantee loan approval. Approval is subject to the bank’s internal policies, eligibility criteria, and verification.
            </Typography>

            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> ✔️ Information Accuracy Declaration </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              You confirm that the information provided by you is true and correct to the best of your knowledge.
            </Typography>

            <div sx={modalStyles.termItem}>
              <Typography sx={modalStyles.termHeading}> 📄 Disclaimer </Typography>
            </div>
            <Typography sx={modalStyles.termText}>
              We are not responsible for any decisions, terms, or agreements made between you and the bank or financial institution.
            </Typography>
          </Box>
        </Box>

        {/* Sticky Buttons */}
        <Box sx={modalStyles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            sx={modalStyles.enquireButton}
            onClick={onEnquireNow}
            disabled={isEnquiring}
          >
            Enquire Now
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            sx={modalStyles.backButton}
            onClick={handleBackToOffers}
          >
            Back to Offers
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const modalStyles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '10px',
    fontFamily: 'Inter-Medium',
    padding: '0',
    width: '90%',
    maxWidth: '600px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    animation: 'fadeIn 0.3s ease-out',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '80vh', // Limit the height of the modal
  },
  header: {
    margin: '20px',
    padding : '10px',
    background: 'linear-gradient(90deg, #F59E0B 20%, #fccb76 80%)',
    borderTopLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    borderBottomLeftRadius: '2px',
    borderTopRightRadius: '2px',
    color: 'white',
  },
  title: {
    fontSize: '26px',
    fontFamily: 'Inter-Medium',
    fontWeight: 700,
    marginBottom: '10px',
    textAlign: 'center',
  },
  text: {
    fontSize: '14px',
    textAlign: 'center',
    fontFamily: 'Inter-Medium',
    marginBottom: '10px',
    color: '#fff',
  },
  scrollableContent: {
    padding: '20px',
    overflowY: 'auto', // Enable scroll if content exceeds max height
    scrollbarWidth: 'none', // Hide scrollbar in Firefox
    msOverflowStyle: 'none',
    flexGrow: 1, // Allow the content to take up remaining space
  },
  termsList: {
    marginBottom: '20px',
  },
  termItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '15px',
  },
  termText: {
    fontSize: '14px',
    fontFamily: 'Inter-Medium',
    fontWeight: 400,
    color: '#333',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  termHeading: {
    fontSize: '16px',
    fontFamily: 'Inter-Medium',
    fontWeight: 600,
    color: '#111827',
    lineHeight: '1.9',
  },
  buttonContainer: {
    display: 'flex',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '20px',
    paddingRight: '20px',
    justifyContent: 'space-between',
    zIndex: 5, // Ensure buttons stay on top
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
  },
  enquireButton: {
    flexGrow: 1,
    marginRight: '10px',
    backgroundColor: '#F59E0B',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    fontFamily: 'Inter-Medium',
    py: 1,
    fontSize: '14px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#D97706',
      boxShadow: 'none',
    },
  },
  backButton: {
    flexGrow: 1,
    borderColor: '#F59E0B',
    fontFamily: 'Inter-Medium',
    color: '#F59E0B',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    '&:hover': {
      borderColor: '#D97706',
      color: '#D97706',
    },
  },
};

export default TermsConditionsModal;